import React, { useState, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import { Col, Row, Spinner } from "reactstrap"
import { AvForm, AvRadioGroup, AvRadio, AvField } from "availity-reactstrap-validation"
import api from "API/Api"
import { SECTIONS, EXPORT_PARAMS_ALL, PRINT_PARAMS_ALL, SMS_PARAMS_ALL, ANNOUNCE_PARAMS_ALL } from "./settingsConfig"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

const CompanySettings = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)
  const [settings, setSettings] = useState({})
  const [formValues, setFormValues] = useState({})

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("settings-by-company/" + id)
        if (r.data.success && r.data.data) { setSettings(r.data.data); setFormValues(r.data.data) }
        else setError("No settings found")
      } catch (e) { setError("Failed to load settings") }
      setPageLoad(false)
    }
    load()
  }, [id])

  // Track radio/select changes for conditional visibility
  const onFieldChange = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e, values) => {
    setLoading(true)
    try {
      values.export_parameters = settings.export_parameters || "[]"
      values.print_parameters = settings.print_parameters || "[]"
      values.sms_parameters = settings.sms_parameters || "[]"
      values.announcement_parameters = settings.announcement_parameters || "[]"
      const r = await api.put("update-company-settings/" + settings.id, values)
      if (r.data.success) { setSubmitted(true); setTimeout(() => setSubmitted(false), 4000) }
    } catch (e) { alert("Failed to update") }
    setLoading(false)
  }

  // Check dep condition - supports multi-value: dep.val is an array
  const isDepMet = (dep) => {
    if (!dep) return true
    const currentVal = String(formValues[dep.field] ?? settings[dep.field] ?? "")
    return dep.val.includes(currentVal)
  }

  // Drag-drop dual list
  const DualList = ({ name, label, allItems, depField }) => {
    // Check if this dual list should be visible
    if (depField && !isDepMet(depField)) return null

    const [f1, setF1] = useState(""); const [f2, setF2] = useState("")
    let selected = []
    try { selected = JSON.parse(settings[name] || "[]") } catch { selected = [] }
    const available = allItems.filter(i => !selected.includes(i))
    const add = (item) => { const n = [...selected, item]; setSettings(p => ({ ...p, [name]: JSON.stringify(n) })) }
    const remove = (item) => { const n = selected.filter(i => i !== item); setSettings(p => ({ ...p, [name]: JSON.stringify(n) })) }
    const onDragEnd = (result) => {
      if (!result.destination) return
      const items = Array.from(selected); const [r] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, r); setSettings(p => ({ ...p, [name]: JSON.stringify(items) }))
    }
    const iStyle = { padding: "8px 12px", marginBottom: "4px", background: "#6b7280", color: "#fff", borderRadius: "4px", cursor: "pointer", fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" }
    return (
      <div style={{ background: "#fff", borderRadius: "10px", padding: "16px", marginBottom: "16px", border: "1px solid #e2e8f0" }}>
        <label style={{ fontWeight: 600, fontSize: "14px", color: "#334155", marginBottom: "12px", display: "block" }}>
          <i className="fas fa-grip-vertical" style={{ marginRight: "6px", opacity: 0.6 }}></i>{label}
        </label>
        <Row>
          <Col md={6}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Available</div>
            <input type="text" className="form-control form-control-sm mb-2" placeholder="Input Filter" value={f1} onChange={e => setF1(e.target.value)} />
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
              {available.filter(i => i.toLowerCase().includes(f1.toLowerCase())).map((item, i) => (
                <div key={i} style={iStyle} onClick={() => add(item)}>{item} <i className="fas fa-plus" style={{ fontSize: "10px" }}></i></div>
              ))}
            </div>
          </Col>
          <Col md={6}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Selected (Drag to reorder)</div>
            <input type="text" className="form-control form-control-sm mb-2" placeholder="Input Filter" value={f2} onChange={e => setF2(e.target.value)} />
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={name}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} style={{ maxHeight: "250px", overflowY: "auto", minHeight: "40px" }}>
                    {selected.filter(i => i.toLowerCase().includes(f2.toLowerCase())).map((item, index) => (
                      <Draggable key={item} draggableId={item} index={index}>
                        {(prov, snap) => (
                          <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                            style={{ ...iStyle, background: snap.isDragging ? "#4b5563" : "#6b7280", ...prov.draggableProps.style }}>
                            <span><i className="fas fa-grip-vertical" style={{ marginRight: "8px", opacity: 0.5 }}></i>{item}</span>
                            <i className="fas fa-times" style={{ fontSize: "10px", cursor: "pointer" }} onClick={(ev) => { ev.stopPropagation(); remove(item) }}></i>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Col>
        </Row>
      </div>
    )
  }

  if (pageLoad) return <div className="dashBody"><div className="text-center py-5"><Spinner color="primary" style={{ width: "3rem", height: "3rem" }} /><p className="mt-3 text-muted">Loading Settings...</p></div></div>
  if (error || !settings.id) return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader"><Row><Col md={6}><h4>Center Settings</h4></Col><Col md={6} style={{ textAlign: "right" }}><Link to="/companies" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link></Col></Row></div>
      <div className="dashContent"><div className="alert alert-warning mt-3"><i className="fas fa-exclamation-triangle me-2"></i>{error || "Settings not found"}</div></div>
    </div>
  )

  const renderField = (f) => {
    if (f.dep && !isDepMet(f.dep)) return null
    const cardStyle = { background: "#fff", borderRadius: "8px", padding: "12px 16px", marginBottom: "10px", border: "1px solid #e2e8f0" }
    if (f.t === "radio") {
      return (
        <Col md={6} key={f.n}>
          <div style={cardStyle}>
            <AvRadioGroup inline name={f.n} label={f.l} value={String(formValues[f.n] ?? settings[f.n] ?? f.def ?? "")} errorMessage="Pick one!"
              onChange={(e) => onFieldChange(f.n, e.target.value)}>
              {f.o.map((opt, i) => <AvRadio key={i} label={opt.l} value={String(opt.v)} />)}
            </AvRadioGroup>
            {f.note && <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}><i className="fas fa-info-circle me-1"></i>{f.note}</small>}
          </div>
        </Col>
      )
    }
    if (f.t === "select") {
      return (
        <Col md={6} key={f.n}>
          <div style={cardStyle}>
            <AvField type="select" name={f.n} label={f.l} value={settings[f.n] || ""} onChange={(e) => onFieldChange(f.n, e.target.value)}>
              <option value="">Select</option>
              {f.o.map((opt, i) => <option key={i} value={opt.v}>{opt.l}</option>)}
            </AvField>
          </div>
        </Col>
      )
    }
    return (
      <Col md={6} key={f.n}>
        <div style={cardStyle}>
          <AvField name={f.n} label={f.l} value={settings[f.n] || f.def || ""} type="text" />
          {f.note && <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}><i className="fas fa-info-circle me-1"></i>{f.note}</small>}
        </div>
      </Col>
    )
  }

  const SectionHead = ({ sec }) => (
    <div style={{ padding: "12px 16px", marginBottom: "14px", marginTop: "18px", borderRadius: "8px",
      background: `linear-gradient(135deg, ${sec.color}12, ${sec.color}08)`, borderLeft: `4px solid ${sec.color}` }}>
      <h5 style={{ margin: 0, color: sec.color, fontWeight: 700, fontSize: "15px" }}>
        <i className={`fas ${sec.icon}`} style={{ marginRight: "8px" }}></i>{sec.title}
      </h5>
    </div>
  )

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Center Settings <small className="text-muted" style={{ fontSize: "13px" }}>[<span className="text-danger">* required</span>]</small></h4></Col>
          <Col md={6} style={{ textAlign: "right" }}><Link to="/companies" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link></Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal" onValidSubmit={handleSubmit}>
          {SECTIONS.map((sec, si) => (
            <div key={si}>
              <SectionHead sec={sec} />
              <Row>{sec.fields.map(f => renderField(f))}</Row>
            </div>
          ))}

          {/* 139. Export Parameters */}
          <SectionHead sec={{ title: "139. Export Parameters", icon: "fa-file-export", color: "#14b8a6" }} />
          <DualList name="export_parameters" label="Export Parameters (Drag to reorder)" allItems={EXPORT_PARAMS_ALL} />

          {/* 141. SMS Parameters */}
          <SectionHead sec={{ title: "141. SMS Parameters", icon: "fa-sms", color: "#0891b2" }} />
          <DualList name="sms_parameters" label="SMS Parameters (Drag to reorder)" allItems={SMS_PARAMS_ALL} />

          {/* 142. Print Parameters */}
          <SectionHead sec={{ title: "142. Print Parameters", icon: "fa-print", color: "#d97706" }} />
          <DualList name="print_parameters" label="Print Parameters (Drag to reorder)" allItems={PRINT_PARAMS_ALL} />

          {/* 152. Announcement Parameters (dep #151=Yes) */}
          <DualList name="announcement_parameters" label="152. Announcement Parameters (Drag to reorder)" allItems={ANNOUNCE_PARAMS_ALL}
            depField={{ field: "announce_purchase_params", val: ["1"] }} />

          {/* Submit */}
          <div className="mb-3 row mt-4">
            <div className="col-6">
              {submitted && <div className="alert alert-success"><strong>Settings Updated Successfully!</strong></div>}
            </div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner size="sm" /><span style={{ paddingLeft: "10px" }}>Updating...</span></> : <span><i className="fas fa-save"></i> Update Settings</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default CompanySettings
